import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useMemo } from "react";
import { LazyMarkdown } from "@/components/site/LazyMarkdown";
import { SiteShell } from "@/components/site/SiteShell";
import { ArticleCTA } from "@/components/site/ArticleCTA";
import { ArticleHeroFrame } from "@/components/site/ArticleHeroFrame";
import { ResourceCover } from "@/components/resources/ResourceCover";
import { ArticleSummary } from "@/components/resources/ArticleSummary";
import { LlantasEvidenceModule } from "@/components/resources/LlantasEvidenceModule";
import { MigrationClusterLinks } from "@/components/resources/MigrationClusterLinks";
import {
  ArticleEditorialContext,
  ArticleVerifiedSources,
  LinkableResourcePrompt,
} from "@/components/resources/ArticleEditorialContext";
import { IntentShiftCallout } from "@/components/site/IntentShiftCallout";
import { SystemBridgeCallout } from "@/components/site/SystemBridgeCallout";
import { AuthorCard } from "@/components/site/AuthorCard";
import { ReadingProgress } from "@/components/site/ReadingProgress";
import { ArticleTOC } from "@/components/site/ArticleTOC";
import { ShareButtons } from "@/components/site/ShareButtons";
import { buildHead, breadcrumbJsonLd, howToJsonLd, ROBOTS_NOINDEX_NOFOLLOW } from "@/lib/seo";
import { SITE_URL } from "@/lib/i18n";
import { publicResourcePageQuery } from "@/lib/query-registry";
import { RelatedResources } from "@/components/site/RelatedResources";
import { faqJsonLdFromBody, formatUpdated, howToStepsForSlug } from "@/lib/article-utils";
import {
  extractHeadings,
  createSlugQueue,
  extractHtmlHeadings,
  injectHtmlHeadingIds,
  isHtmlBody as detectHtmlBody,
  splitHtmlBodyForCTA,
  demoteH1ToH2,
} from "@/lib/article-toc";
import { resolveResourceRedirect, resolveCrossLocaleRedirect } from "@/lib/resource-redirects";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import {
  getResourceEditorialStrategy,
  normalizeEditorialBody,
} from "@/lib/resource-editorial-strategy";
import { isSpanishTranslationMissing } from "@/lib/resource-translation";

function splitBodyForCTA(body: string): [string, string] {
  if (!body) return ["", ""];
  const h2Indices: number[] = [];
  const lines = body.split("\n");
  let cursor = 0;
  for (const line of lines) {
    if (/^##\s/.test(line)) h2Indices.push(cursor);
    cursor += line.length + 1;
  }
  if (h2Indices.length >= 2) {
    const splitAt = h2Indices[1];
    return [body.slice(0, splitAt).trimEnd(), body.slice(splitAt)];
  }
  const half = Math.floor(body.length / 2);
  const after = body.indexOf("\n\n", half);
  if (after > 0) return [body.slice(0, after), body.slice(after + 2)];
  return [body, ""];
}

export const Route = createFileRoute("/es/recursos/$slug")({
  loader: async ({ params, context }) => {
    await setEdgeHtmlCache();
    const crossLocale = resolveCrossLocaleRedirect(params.slug, "es");
    if (crossLocale) throw redirect({ href: crossLocale, statusCode: 301 });

    const redirectTo = resolveResourceRedirect(params.slug);
    if (redirectTo) {
      throw redirect({
        to: "/es/recursos/$slug",
        params: { slug: redirectTo },
        statusCode: 301,
      });
    }

    const page = await context.queryClient.ensureQueryData(
      publicResourcePageQuery({ slug: params.slug, locale: "es", limit: 3 }),
    );
    if (!page.resource) throw notFound();

    // No Spanish body yet: serving the English text under /es/ with lang
    // es-MX and hreflang es declares a translation that does not exist.
    // Send readers to the English original until the translation ships.
    // 302 because this is temporary by definition.
    if (isSpanishTranslationMissing(page.resource as any)) {
      const enSlug = ((page.resource as any).slug as string | undefined) ?? params.slug;
      throw redirect({ href: `/resources/${enSlug}`, statusCode: 302 });
    }

    const canonicalEsSlug = (page.resource as any).slug_es as string | null | undefined;
    if (canonicalEsSlug && params.slug !== canonicalEsSlug) {
      throw redirect({
        to: "/es/recursos/$slug",
        params: { slug: canonicalEsSlug },
        statusCode: 301,
      });
    }

    return page;
  },
  head: ({ loaderData, params }) => {
    const r = loaderData?.resource;
    // Loader threw notFound(): emit the single noindex directive here so the
    // 404 page does not inherit the root route's index default.
    if (!r) {
      return {
        meta: [
          { title: "Recurso no encontrado · Shugert" },
          { name: "robots", content: ROBOTS_NOINDEX_NOFOLLOW },
        ],
        links: [],
      };
    }

    const slug = params.slug;
    const enSlug = (r as any).slug as string;
    const editorial = getResourceEditorialStrategy(r as any);
    const headline = editorial.displayTitle?.es || r.title_es || r.title_en;
    const description = editorial.dek?.es || r.excerpt_es || r.excerpt_en || headline;
    const normalizedBody = normalizeEditorialBody(r.body_es || r.body_en || "", r as any, "es");

    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline,
      description,
      image: (r.cover_image_url_es ?? r.cover_image_url) ? [r.cover_image_url_es ?? r.cover_image_url] : undefined,
      datePublished: r.created_at,
      dateModified: (r as any).reviewed_at ?? r.updated_at,
      author: {
        "@type": "Person",
        name: "Samuel Noriega",
        url: `${SITE_URL}/es/nosotros/samuel-noriega`,
        jobTitle: "Fundador, Shugert Marketing",
      },
      publisher: {
        "@type": "Organization",
        name: "Shugert",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
      },
      mainEntityOfPage: `${SITE_URL}/es/recursos/${slug}`,
    };

    const faqLd = faqJsonLdFromBody(normalizedBody);
    const howToCfg = howToStepsForSlug(slug, "es");
    const howToLd = howToCfg ? howToJsonLd(howToCfg.name, howToCfg.description, howToCfg.steps) : null;

    return buildHead({
      locale: "es",
      title: `${editorial.metaTitle?.es || editorial.displayTitle?.es || ((r as any).meta_title_es as string | null | undefined) || r.title_es || r.title_en} · Shugert`,
      description,
      path: `/es/recursos/${slug}`,
      altPath: `/resources/${enSlug}`,
      type: "article",
      ogImage: r.cover_image_url_es ?? r.cover_image_url ?? undefined,
      author: "Samuel Noriega",
      publishedTime: r.created_at,
      modifiedTime: (r as any).reviewed_at ?? r.updated_at,
      articleAuthor: "Samuel Noriega",
      articleSection: (((r.tags_es ?? r.tags_en) ?? []) as string[])[0],
      jsonLd: [
        articleJsonLd,
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Recursos", path: "/es/recursos" },
          { name: headline, path: `/es/recursos/${slug}` },
        ]),
        ...(faqLd ? [faqLd] : []),
        ...(howToLd ? [howToLd] : []),
      ],
    });
  },
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">404</p>
        <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-foreground">Recurso no encontrado</h1>
        <Link to="/es/recursos" className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background">
          Ver recursos
        </Link>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-semibold text-foreground">Algo salió mal</h1>
        <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
        <Link to="/es/recursos" className="mt-8 inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground">
          Volver a recursos
        </Link>
      </div>
    </SiteShell>
  ),
  component: () => (
    <SiteShell>
      <ResourceArticleEs />
    </SiteShell>
  ),
});

function ResourceArticleEs() {
  const { resource, related } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const r = resource;
  const editorial = getResourceEditorialStrategy(r as any);

  const title = editorial.displayTitle?.es || r.title_es || r.title_en;
  const excerpt = editorial.dek?.es || r.excerpt_es || r.excerpt_en;
  const rawBody = r.body_es || r.body_en;
  const body = normalizeEditorialBody(rawBody ?? "", r as any, "es");
  const tags = ((r.tags_es ?? r.tags_en) ?? []) as string[];
  const tag = tags[0] ?? "";

  const isHtmlBody = detectHtmlBody(body);
  const normalizedBody = useMemo(
    () => (isHtmlBody ? demoteH1ToH2(body) : body),
    [body, isHtmlBody],
  );
  const headings = useMemo(
    () => (isHtmlBody ? extractHtmlHeadings(normalizedBody) : extractHeadings(normalizedBody)),
    [normalizedBody, isHtmlBody],
  );
  const htmlBodyWithIds = useMemo(
    () => (isHtmlBody ? injectHtmlHeadingIds(normalizedBody, headings) : normalizedBody),
    [normalizedBody, isHtmlBody, headings],
  );
  const [bodyTop, bodyBottom] = isHtmlBody
    ? splitHtmlBodyForCTA(htmlBodyWithIds)
    : splitBodyForCTA(normalizedBody);
  const shareUrl = `${SITE_URL}/es/recursos/${slug}`;

  const mdComponents = useMemo(() => {
    const queue = createSlugQueue(headings);
    return {
      h1: ({ children, ...props }: any) => <h2 id={queue.next()} {...props}>{children}</h2>,
      h2: ({ children, ...props }: any) => <h2 id={queue.next()} {...props}>{children}</h2>,
      h3: ({ children, ...props }: any) => <h3 id={queue.next()} {...props}>{children}</h3>,
    };
  }, [headings]);

  return (
    <article className="pb-32">
      <ReadingProgress />
      <section className="resource-article-hero border-b border-border" data-surface="light">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:py-24">
          <div>
            <Link to="/es/recursos" className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground">
              ← Recursos
            </Link>
            <ArticleHeroFrame tag={tag} readingMinutes={r.reading_minutes} locale="es" />
            <h1 className="mt-6 max-w-4xl text-balance font-display text-4xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            {excerpt ? <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">{excerpt}</p> : null}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <span>Actualizado {formatUpdated((r as any).reviewed_at ?? r.updated_at, "es")}</span>
              <span>{r.reading_minutes ?? 0} min</span>
              <span>Samuel Noriega</span>
            </div>
            <AuthorCard variant="byline" locale="es" publishedAt={r.created_at} reviewedAt={(r as any).reviewed_at ?? r.updated_at} />
            <ShareButtons url={shareUrl} title={title} locale="es" className="mt-6" />
          </div>
          <div className="overflow-hidden rounded-[2rem] border border-border shadow-[0_24px_80px_rgba(0,0,0,0.10)]">
            <div className="aspect-[16/10]">
              <ResourceCover resource={r as any} locale="es" />
            </div>
          </div>
        </div>
      </section>

      <ArticleSummary excerpt={excerpt} headings={headings as any} locale="es" />
      <ArticleEditorialContext resource={r as any} locale="es" />
      <LlantasEvidenceModule slug={slug} locale="es" />
      <MigrationClusterLinks currentSlug={slug} locale="es" />

      <div className={`mx-auto mt-16 px-6 ${headings.length >= 3 ? "max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_240px] lg:gap-14" : "max-w-3xl"}`}>
        <div className={headings.length >= 3 ? "min-w-0" : ""}>
          <div className="prose prose-lg prose-neutral max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:mt-16 prose-h2:border-t prose-h2:border-border prose-h2:pt-12 prose-h2:text-3xl prose-h2:scroll-mt-28 prose-h3:mt-10 prose-h3:text-xl prose-h3:scroll-mt-28 prose-p:leading-[1.82] prose-li:my-2 prose-a:font-medium prose-a:text-foreground prose-a:underline-offset-4 prose-strong:text-foreground prose-table:overflow-hidden prose-table:rounded-xl prose-table:border prose-table:border-border prose-th:bg-card prose-th:p-3 prose-td:border-t prose-td:border-border prose-td:p-3 prose-code:rounded prose-code:bg-card prose-code:px-1.5 prose-code:py-0.5 prose-code:font-mono prose-code:text-[0.88em] prose-blockquote:my-10 prose-blockquote:rounded-2xl prose-blockquote:border-l-4 prose-blockquote:border-foreground/30 prose-blockquote:bg-card/60 prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:not-italic prose-blockquote:font-medium dark:prose-invert">
            {isHtmlBody ? <div dangerouslySetInnerHTML={{ __html: bodyTop }} /> : <LazyMarkdown components={mdComponents}>{bodyTop}</LazyMarkdown>}
            {bodyBottom ? (
              <ArticleCTA
                variant="inline"
                tag={tag}
                style={(r.cta_style as any) ?? "blue-glow"}
                eyebrow={r.cta_eyebrow_es}
                title={r.cta_title_es}
                body={r.cta_body_es}
                primaryLabel={r.cta_primary_label_es}
                primaryHref={r.cta_primary_href_es}
              />
            ) : null}
            {bodyBottom ? <IntentShiftCallout locale="es" /> : null}
            {bodyBottom ? (isHtmlBody ? <div dangerouslySetInnerHTML={{ __html: bodyBottom }} /> : <LazyMarkdown components={mdComponents}>{bodyBottom}</LazyMarkdown>) : null}
          </div>

          <ShareButtons url={shareUrl} title={title} locale="es" className="mt-12" />
          <ArticleVerifiedSources strategy={editorial} locale="es" />
          <LinkableResourcePrompt strategy={editorial} locale="es" title={title} url={shareUrl} />

          <AuthorCard variant="full" locale="es" publishedAt={r.created_at} reviewedAt={(r as any).reviewed_at ?? r.updated_at} />
          <SystemBridgeCallout locale="es" />
          <RelatedResources items={related ?? []} locale="es" />
          <ArticleCTA
            variant="footer"
            style={(r.cta_style as any) ?? "blue-glow"}
            eyebrow={r.cta_eyebrow_es}
            title={r.cta_title_es}
            body={r.cta_body_es}
            primaryLabel={r.cta_primary_label_es}
            primaryHref={r.cta_primary_href_es}
          />
        </div>

        {headings.length >= 3 ? <ArticleTOC items={headings} locale="es" /> : null}
      </div>
    </article>
  );
}
